import { MemoryEntry, MemoryDetail } from '../types/legacyControls';

const API_BASE = (import.meta.env as unknown as Record<string, string>).VITE_API_HOST || 'http://localhost:3001';

export async function fetchMemories(sessionId?: string): Promise<MemoryEntry[]> {
  try {
    const url = sessionId 
      ? `${API_BASE}/api/memory/list?sessionId=${encodeURIComponent(sessionId)}`
      : `${API_BASE}/api/memory/list`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    
    if (!result.success || !result.data || !result.data.memories) {
      throw new Error('Invalid response format from memory API');
    }

    return result.data.memories.map((memory: Record<string, unknown>) => ({
      id: memory.id,
      ts: new Date((memory.metadata as Record<string, unknown>).createdAt as string),
      type: (memory.metadata as Record<string, unknown>).source as string || 'Unknown',
      excerpt: (memory.text as string).length > 100 ? (memory.text as string).substring(0, 100) + '...' : memory.text as string,
      tags: memory.tags || []
    }));
  } catch (error) {
    console.error('Error fetching memories:', error);
    return [];
  }
}

export async function fetchMemoryDetail(memoryId: string): Promise<MemoryDetail> {
  try {
    const response = await fetch(`${API_BASE}/api/memory/${memoryId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    
    if (!result.success || !result.data) {
      throw new Error('Invalid response format from memory detail API');
    }

    const memory = result.data;
    return {
      id: memory.id,
      content: memory.text,
      metadata: {
        source: memory.metadata.source,
        confidence: 85,
        relatedTaskId: memory.sessionId,
        agent: memory.metadata.source,
        status: 'completed',
        progress: 100,
        createdAt: new Date(memory.metadata.createdAt),
        updatedAt: new Date(memory.metadata.createdAt)
      }
    };
  } catch (error) {
    console.error('Error fetching memory detail:', error);
    throw error;
  }
}

export async function addMemory(key: string, text: string, tags: string[] = [], sessionId?: string): Promise<string> {
  try {
    const response = await fetch(`${API_BASE}/api/memory/add`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        key,
        text,
        tags,
        sessionId,
        metadata: {
          source: 'frontend'
        }
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    
    if (!result.success || !result.data || !result.data.id) {
      throw new Error('Invalid response format from add memory API');
    }

    return result.data.id;
  } catch (error) {
    console.error('Error adding memory:', error);
    throw error;
  }
}

export async function deleteMemory(memoryId: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}/api/memory/${memoryId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    return result.success || false;
  } catch (error) {
    console.error('Error deleting memory:', error);
    throw error;
  }
}

export async function queryMemories(query: string, sessionId?: string, topK: number = 10): Promise<MemoryEntry[]> {
  try {
    const response = await fetch(`${API_BASE}/api/memory/query`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        query,
        sessionId,
        topK
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    
    if (!result.success || !result.data || !result.data.records) {
      throw new Error('Invalid response format from query memory API');
    }

    return result.data.records.map((memory: Record<string, unknown>) => ({
      id: memory.id,
      ts: new Date((memory.metadata as Record<string, unknown>).createdAt as string),
      type: (memory.metadata as Record<string, unknown>).source as string || 'Unknown',
      excerpt: (memory.text as string).length > 100 ? (memory.text as string).substring(0, 100) + '...' : memory.text as string,
      tags: memory.tags || []
    }));
  } catch (error) {
    console.error('Error querying memories:', error);
    return [];
  }
}
